import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { useTeam } from '../../context/TeamContext';
import { formatCurrency } from '../../utils/helpers';

const TAG_LIBRARY_KEY = 'saleswork_demo_tag_library';
const DEFAULT_TAG_COLORS = ['blue', 'green', 'purple', 'orange', 'red', 'gray'];
const TAG_COLOR_CLASSES = {
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  green: 'bg-green-50 text-green-700 border-green-200',
  purple: 'bg-purple-50 text-purple-700 border-purple-200',
  orange: 'bg-orange-50 text-orange-700 border-orange-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  gray: 'bg-gray-100 text-gray-700 border-gray-200'
};

const normalizeTag = tag => {
  if (typeof tag === 'string') {
    return { id: tag, name: tag, color: '#DBEAFE' };
  }

  return {
    id: tag?.id || tag?.name || '',
    name: tag?.name || '',
    color: tag?.color || '#DBEAFE'
  };
};

const getTagMeta = tagName => {
  const name = String(tagName || '').trim();

  try {
    const raw = localStorage.getItem(TAG_LIBRARY_KEY);
    const library = raw ? JSON.parse(raw) : [];
    const existing = Array.isArray(library)
      ? library.find(tag => tag?.name?.toLowerCase() === name.toLowerCase())
      : null;

    return existing || { name, color: 'gray' };
  } catch {
    return { name, color: 'gray' };
  }
};

const getTagClassName = color => TAG_COLOR_CLASSES[color] || TAG_COLOR_CLASSES.gray;

const getDomainFromUrl = url => {
  try {
    if (!url) return '';
    const normalized = url.startsWith('http') ? url : `https://${url}`;
    return new URL(normalized).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const getLogoUrlFromWebsite = website => {
  const domain = getDomainFromUrl(website);
  return domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128` : '';
};

const getOsloCalendarDate = date => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Oslo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);

  return parts.reduce((result, part) => {
    if (part.type === 'year') result.year = part.value;
    if (part.type === 'month') result.month = part.value;
    if (part.type === 'day') result.day = part.value;
    return result;
  }, {});
};

const getCalendarDayNumber = date => {
  const { year, month, day } = getOsloCalendarDate(date);
  return Date.UTC(Number(year), Number(month) - 1, Number(day));
};

const formatRelativeDate = value => {
  if (!value) return 'Ukjent dato';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Ukjent dato';

  const now = new Date();
  const dayDifference = Math.round(
    (getCalendarDayNumber(now) - getCalendarDayNumber(date)) / 86400000
  );
  const elapsedMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);

  if (dayDifference === 0) {
    if (elapsedMinutes >= 0 && elapsedMinutes < 60) {
      return elapsedMinutes < 1 ? 'Nå nettopp' : `For ${elapsedMinutes} min siden`;
    }

    return 'I dag';
  }

  if (dayDifference === 1) return 'I går';

  return new Intl.DateTimeFormat('nb-NO', {
    timeZone: 'Europe/Oslo',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(date);
};

const getOwnerId = deal => (
  deal?.owner_user_id ||
  deal?.user_id ||
  deal?.assigned_user_id ||
  deal?.ownerId ||
  null
);

const getOwnerLabel = (deal, teamUsers) => {
  const owner = deal?.owner || deal?.ownerName;
  const ownerId = getOwnerId(deal);
  const matchedOwner = ownerId
    ? teamUsers.find(user => user?.user_id === ownerId || user?.id === ownerId)
    : null;

  if (matchedOwner?.name) return matchedOwner.name;
  if (matchedOwner?.email) return matchedOwner.email;
  if (typeof owner === 'string' && owner.trim() && !ownerId) return owner;
  if (owner?.name && !ownerId) return owner.name;
  if (owner?.email && !ownerId) return owner.email;
  if (ownerId) return 'Ukjent bruker';
  return 'Ikke tildelt';
};

const DealCard = ({
  deal,
  company,
  contact,
  onEdit,
  onDelete,
  onArchive,
  stageName,
  displayMode
}) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const { users: teamUsers = [] } = useTeam();

  const isCustomerDeal =
    deal?.isCustomer === true ||
    deal?.convertedToCustomer === true ||
    deal?.customerStatus === 'active' ||
    deal?.status === 'customer' ||
    deal?.status === 'closed_won' ||
    deal?.status === 'Vunnet';

  const customerDate =
    deal?.convertedAt ||
    deal?.closedWonAt ||
    deal?.wonAt ||
    null;

  const normalizedTags = Array.isArray(deal.tags) ? deal.tags : [];
  const tagsToDisplay = normalizedTags
    .map(normalizeTag)
    .filter(tag => tag.name)
    .slice(0, 2);

  const displayCompanyName =
    company?.name ||
    deal.companyName ||
    deal.company_name ||
    deal.company ||
    '';

  const displayContactName =
    contact?.name ||
    deal.contactName ||
    deal.contact_name ||
    deal.name ||
    '';

  const primaryDisplayName = displayMode === 'contact' ? 'contact' : 'company';
  const mainTitle = primaryDisplayName === 'contact'
    ? displayContactName || displayCompanyName || 'Ukjent kontakt'
    : displayCompanyName || displayContactName || 'Ukjent firma';
  const subTitle = primaryDisplayName === 'contact'
    ? displayCompanyName && displayCompanyName !== mainTitle ? displayCompanyName : ''
    : displayContactName && displayContactName !== mainTitle ? displayContactName : '';

  const displayLogoUrl =
    deal.logoUrl ||
    deal.companyLogo ||
    company?.logoUrl ||
    company?.companyLogo ||
    (deal.website ? getLogoUrlFromWebsite(deal.website) : '') ||
    (company?.website ? getLogoUrlFromWebsite(company.website) : '');

  const dealDate =
    deal?.created_at ||
    deal?.createdAt ||
    deal?.updated_at ||
    deal?.updatedAt;

  const handleDragStart = event => {
    const dealId = deal?.deal_id || deal?.id;
    event.dataTransfer?.setData('dealId', dealId || '');
  };

  return (
    <motion.div
      layout
      draggable
      onDragStart={handleDragStart}
      onClick={() => onEdit(deal)}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
      className="bg-white border border-slate-200 rounded-lg px-3 py-2 shadow-sm cursor-pointer group relative overflow-hidden hover:border-blue-400 transition-all"
    >
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${deal.value > 100000 ? 'bg-emerald-500' : 'bg-blue-500'}`} />

      <div className="pl-2">
        <div className="flex items-start gap-2 mb-1.5">
          <div className="w-7 h-7 rounded bg-slate-50 border border-slate-100 flex items-center justify-center text-base flex-shrink-0">
            {displayLogoUrl && !logoFailed ? (
              <img src={displayLogoUrl} alt="" className="w-5 h-5 object-contain" onError={() => setLogoFailed(true)} />
            ) : '🏢'}
          </div>

          <div className="min-w-0 flex-1 mt-0.5">
            <h4 className="font-bold text-slate-900 text-sm truncate leading-tight mb-0.5">{mainTitle}</h4>
            {subTitle && <p className="text-xs text-slate-500 truncate">{subTitle}</p>}
          </div>

          <button
            type="button"
            onMouseDown={event => event.stopPropagation()}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              onArchive?.(deal);
            }}
            className="flex items-center gap-1 text-slate-400 hover:text-red-600 opacity-100 transition-opacity mt-1"
            title="Arkiver kort"
            aria-label="Arkiver kort"
          >
            <SafeIcon icon="🗑️" className="w-4 h-4" />
            <span className="text-[10px] font-bold">Arkiver</span>
          </button>
        </div>

        {(tagsToDisplay.length > 0 || isCustomerDeal) && (
          <div className="mb-2 flex flex-wrap gap-1">
            {tagsToDisplay.map(tag => (
              <span key={tag.id} className={`inline-flex px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide border ${getTagClassName(getTagMeta(tag.name).color)}`}>
                {tag.name}
              </span>
            ))}
            {normalizedTags.length > 2 && (
              <span className="inline-flex px-2 py-0.5 rounded text-[9px] font-bold bg-slate-100 text-slate-500 border border-slate-200">
                +{normalizedTags.length - 2}
              </span>
            )}
            {isCustomerDeal && (
              <span className="inline-flex items-center rounded-md border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-green-700" title={customerDate ? `Kunde siden ${customerDate}` : 'Kunde'}>
                Kunde
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-slate-50">
          <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
            <SafeIcon icon="🕒" className="w-3 h-3" />
            <span>{formatRelativeDate(dealDate)}</span>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-black text-slate-900">{formatCurrency(deal.value)}</span>
            <span className="text-[10px] font-semibold text-slate-500 max-w-24 truncate" title={getOwnerLabel(deal, teamUsers)}>
              {getOwnerLabel(deal, teamUsers)}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DealCard;